# Step 1: Display button

We strongly recommend that your software models its apply form in the following manner:

- If the `seek-prefill-id` parameter is present,
  skip ahead to handle authorization in the [Allow access] scenario.

- If the `seek-prefill-id` parameter is not present,
  display the Apply with SEEK button with a `redirectUri` back to the current page.

This simplified model enables your software to seamlessly handle all scenarios described below with minimal effort.

```mermaid
flowchart TD
SeekAd([SEEK job ad])
Another([another source])
FormBefore[apply form without seek-prefill-id]
FormAfter([apply form with seek-prefill-id])
SeekAuth[Apply with SEEK authorization page]

SeekAd-- arrive without pre-authorization (1) --->FormBefore
SeekAd-- pre-authorization (2) --->SeekAuth

Another-- arrive (3) --->FormBefore

FormBefore-- click Apply with SEEK button --->SeekAuth

SeekAuth-- allow access -->FormAfter
SeekAuth-- not now --->FormBefore
```

[Allow access]: /use-cases/apply-with-seek/handle-authorization#allow-access

## Pre-fill scenarios

Apply with SEEK can be used by a candidate to pre-fill in three scenarios:

1. When a candidate links out from a SEEK job ad to your apply form,
   and they manually click the Apply with SEEK button on the form.
2. When a candidate links out from a SEEK job ad to your apply form,
   and they have pre-authorized your software to pre-fill the form.
3. When a candidate arrives on your apply form directly or through another channel,
   and they manually click the Apply with SEEK button on the form.

### Scenario 1: Candidate arrives from SEEK without pre-authorization

```mermaid Full sequence
sequenceDiagram
  participant Candidate
  participant SEEK
  participant Partner
  participant SEEKAPI as SEEK API

  Candidate->>SEEK: View job ad
  Candidate->>SEEK: Click Apply button
  SEEK->>Partner: Redirect to partner apply form with seek-token
  activate Partner
  Partner->>SEEKAPI: Request Apply with SEEK button with seek-token
  Partner-->>Candidate: Display form with Apply with SEEK button
  deactivate Partner
```

If the candidate has [linked out] from a SEEK job ad,
you will receive a `seek-token` parameter appended to the query string of the [`applicationUri`]:

```text
https://example.com/position/123/?seek-token=eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9
```

Propagate the token to the [`applyWithSeekButton` query] and subsequent [signals] like apply form submission.
It is unique to that specific candidate, job ad, and session,
and must not be reused outside of the context of their current application.

[`applicationUri`]: /schema/named-type/ApplicationMethod#/field/applicationUri
[`applyWithSeekButton` query]: /schema/query/applyWithSeekButton
[linked out]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form
[signals]: /use-cases/apply-with-seek/send-application-complete-signal

```graphql
query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
```

```jsonc default
{
  "input": {
    // The HTTPS URL to redirect the candidate to after authorization.
    // Your software may embed dynamic state in the query string for CSRF protection.
    "redirectUri": "https://example.com/position/123/apply",

    // The identifier for the SEEK hirer that is filling the position.
    // "<Partner Name> on behalf of <Hirer Name>" will be displayed on the authorization page.
    "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",

    // The HTTPS URL of your apply form for the specific position.
    "applicationUri": "https://example.com/position/123/apply",

    // An optional hirer-provided free-text job reference.
    "seekHirerJobReference": "REQ-65558",

    // The seek-token received as part of the query string when the candidate arrived from SEEK.
    "token": "eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9"
  }
}
```

```json
{
  "applyWithSeekButton": {
    "buttonLabel": "Apply with SEEK",
    "images": {
      "accent": "https://integration.seek.com/assets/awsk-button-assets/button-images/seek/en/standard-accent.svg"
    },
    "url": "https://au.seek.com/awsk/authorize?id=00000000-0000-4000-8000-000000000000"
  }
}
```

### Scenario 2: Candidate arrives from SEEK with pre-authorization

```mermaid Full sequence
sequenceDiagram
  participant Candidate
  participant SEEK
  participant Partner

  Candidate->>SEEK: View job ad
  Candidate->>SEEK: Click Apply button
  SEEK-->>Candidate: Redirect to Apply with SEEK authorization page
  Candidate->>SEEK: Click Allow access button
  SEEK->>Partner: Redirect to partner with seek-prefill-id + seek-token
```

If your apply page receives a `seek-prefill-id` parameter in the query string,
skip ahead to handle authorization in the [Allow access] scenario.

This is a variation of Scenario 1 where the candidate has [linked out] from a SEEK job ad,
but they have additionally pre-authorized your software to pre-fill the form using their SEEK Profile.
You may test this scenario by posting a job ad using your [test hirer account] with a position title that contains the string `[PREAUTH]`.

As with Scenario 1,
propagate the `seek-token` parameter to subsequent [signals] like apply form submission.

[test hirer account]: /introduction#integration-process

### Scenario 3: Candidate arrives from another source

```mermaid Full sequence
sequenceDiagram
  participant Candidate
  participant Partner
  participant SEEKAPI as SEEK API

  Candidate->>Partner: View partner apply form
  activate Partner
  Partner->>SEEKAPI: Request Apply with SEEK button without seek-token
  Partner-->>Candidate: Display form with Apply with SEEK button
  deactivate Partner
```

Where a candidate has arrived at your apply form from another source
(direct link, company career site, etc.),
the `seek-token` parameter will not be present in the query string and should be omitted from the [`applyWithSeekButton` query].

```graphql
query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
```

```jsonc default
{
  "input": {
    // The HTTPS URL to redirect the candidate to after authorization.
    // Your software may embed dynamic state in the query string for CSRF protection.
    "redirectUri": "https://example.com/position/123/apply",

    // The identifier for the SEEK hirer that is filling the position.
    // "<Partner Name> on behalf of <Hirer Name>" will be displayed on the authorization page.
    "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",

    // The HTTPS URL of your apply form for the specific position.
    "applicationUri": "https://example.com/position/123/apply",

    // An optional hirer-provided free-text job reference.
    "seekHirerJobReference": "REQ-65558"
  }
}
```

```json
{
  "applyWithSeekButton": {
    "buttonLabel": "Apply with SEEK",
    "images": {
      "accent": "https://integration.seek.com/assets/awsk-button-assets/button-images/seek/en/standard-accent.svg"
    },
    "url": "https://au.seek.com/awsk/authorize?id=00000000-0000-4000-8000-000000000000"
  }
}
```

## Render the button

Call the [`applyWithSeekButton` query] each time your apply form is loaded without a `seek-prefill-id` parameter in the query string.
It provides display assets for the Apply with SEEK button and a single-use authorization URL to link to.
The authorization page prompts the candidate to share their SEEK Profile for the purpose of pre-filling your apply form.

import { Recommended } from '../../../components/Recommended/Recommended';

### Option 1: SEEK button <Recommended />

We recommend this option as it presents consistent and recognisable branding to the candidate while reducing ongoing maintenance effort.

Take SEEK’s branded button by selecting one of the [`images`].
You should use [`buttonLabel`] as an accessible [`aria-label`] or [`alt`],
and link the button to the supplied [`url`].

[`alt`]: https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/alt#usage_notes
[`aria-label`]: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/button_role#basic_buttons
[`buttonLabel`]: /schema/named-type/ApplyWithSeekButtonPayload#/field/buttonLabel
[`images`]: /schema/named-type/ApplyWithSeekButtonPayload#/field/images
[`url`]: /schema/named-type/ApplyWithSeekButtonPayload#/field/url

```graphql
query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
```

```jsonc
{
  "input": {
    // The HTTPS URL to redirect the candidate to after authorization.
    // Your software may embed dynamic state in the query string for CSRF protection.
    "redirectUri": "https://example.com/position/123/apply",

    // The identifier for the SEEK hirer that is filling the position.
    // "<Partner Name> on behalf of <Hirer Name>" will be displayed on the authorization page.
    "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",

    // The HTTPS URL of your apply form for the specific position.
    "applicationUri": "https://example.com/position/123/apply",

    // An optional hirer-provided free-text job reference.
    "seekHirerJobReference": "REQ-65558",

    // The seek-token received as part of the query string when the candidate arrived from SEEK.
    "token": "eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9"
  }
}
```

```json
{
  "applyWithSeekButton": {
    "buttonLabel": "Apply with SEEK",
    "images": {
      "accent": "https://integration.seek.com/assets/awsk-button-assets/button-images/seek/en/standard-accent.svg"
    },
    "url": "https://au.seek.com/awsk/authorize?id=00000000-0000-4000-8000-000000000000"
  }
}
```

Here are a few samples using [JSX] pseudocode:

```jsx label="a + img"
<a href={applyWithSeekButton.url}>
  <img
    aria-roledescription="link"
    alt={applyWithSeekButton.buttonLabel}
    src={applyWithSeekButton.images.accent}
  />
</a>
```

```jsx label="button"
<button
  onClick={() => {
    window.location.href = applyWithSeekButton.url;
  }}
  onKeyDown={(event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      window.location.href = applyWithSeekButton.url;
    }
  }}
  style={{
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0
  }}
  role="link"
  aria-label={applyWithSeekButton.buttonLabel}
>
  <img
    aria-hidden="true"
    src={applyWithSeekButton.images.accent}
    alt={applyWithSeekButton.buttonLabel}
  />
</button>
```

[JSX]: https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Frameworks_libraries/React_getting_started

### Option 2: Custom button

We recommend taking [SEEK’s branded button](#option-1-seek-button) as it presents consistent and recognisable branding to the candidate while reducing ongoing maintenance effort.
If your software has technical constraints that necessitate a custom button,
contact us to discuss further.

Your implementation will be required to follow [our brand guidelines],
and dynamically apply the [`buttonLabel`] and [`url`] to the custom element.

Make sure to exclude the [`images`] field from your query.
Your software should [only select the fields it uses] as a best practice to reduce the surface area of your integration.

[only select the fields it uses]: /graphql/in-practice#selecting-fields
[our brand guidelines]: /use-cases/apply-with-seek/brand-guidelines

```graphql
query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    url
  }
}
```

```jsonc
{
  "input": {
    // The HTTPS URL to redirect the candidate to after authorization.
    // Your software may embed dynamic state in the query string for CSRF protection.
    "redirectUri": "https://example.com/position/123/apply",

    // The identifier for the SEEK hirer that is filling the position.
    // "<Partner Name> on behalf of <Hirer Name>" will be displayed on the authorization page.
    "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h",

    // The HTTPS URL of your apply form for the specific position.
    "applicationUri": "https://example.com/position/123/apply",

    // An optional hirer-provided free-text job reference.
    "seekHirerJobReference": "REQ-65558",

    // The seek-token received as part of the query string when the candidate arrived from SEEK.
    "token": "eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9"
  }
}
```

```json
{
  "applyWithSeekButton": {
    "buttonLabel": "Apply with SEEK",
    "url": "https://au.seek.com/awsk/authorize?id=00000000-0000-4000-8000-000000000000"
  }
}
```

## Error handling

The [`applyWithSeekButton` query] will either return all requested button information,
or an [error response].
For example,
your software may receive a `FORBIDDEN` error if it is not authorized to act on behalf of the specified hirer,
or an `INTERNAL_SERVER_ERROR` if there is an unexpected failure on SEEK’s end.

In these error scenarios,
your software should not display an Apply with SEEK button.
The candidate will not have the option to pre-fill your apply form,
but if they linked out from a SEEK job ad,
your software should still [send an application complete signal] once they submit the form.

[error response]: /graphql/error-responses
[send an application complete signal]: /use-cases/apply-with-seek/send-application-complete-signal

## Content localisation

Your software should include the standard [`Accept-Language`] HTTP request header when it executes the [`applyWithSeekButton` query].
We support multiple brands and languages across our markets,
so providing accurate country information and language preferences enables us to provide a more familiar experience for the candidate.

See [content localisation] for more information.

[`Accept-Language`]: https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Accept-Language
[content localisation]: /graphql/in-practice#content-localisation

## Security considerations

### URL redirection

The SEEK API implicitly trusts your software to provide a safe `redirectUri`.
This is the same treatment given to [Job Posting] `applicationUri`s provided for [linking out],
and avoids the maintenance burden of strict URL allowlists.

Follow best practices around URL redirection to protect candidates from phishing attempts.
At a minimum,
your software must have server-side provisions to ensure the `redirectUri` points to a web resource that your organization manages,
and prevent bad actors from manipulating redirection behaviour.

See the [OWASP cheat sheet] for best practices,
and our [Terms of Use] for more information about your obligations.

[Job Posting]: /use-cases/job-posting
[linking out]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form
[OWASP cheat sheet]: https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html
[Terms of Use]: https://au.employer.seek.com/partners/terms-of-use

### Content Security Policy compatibility

Ensure your CSP is compatible with the [Content Security Policy] we recommend for our integrations.

[Content Security Policy] (CSP) is a security feature that helps prevent various types of attacks, including Cross-Site Scripting (XSS) and data injection attacks.
Add the following directive to your CSP to load Apply with SEEK assets from the SEEK API:

```html
Content-Security-Policy: img-src 'self'
https://integration.seek.com/assets/awsk-button-assets/;
```

This sample directive instructs the browser to only load images from your own domain (`self`) and our `button-assets` directory.

[Content Security Policy]: /introduction/assets#content-security-policy-compatibility
